/**
 * Client-side ranking + filtering for the command registry (#1514).
 *
 * Fast, dependency-free matching over a fixed in-memory command list. Ranking
 * tiers (HIGHEST priority first):
 *   0. prefix match on label
 *   1. substring match on label
 *   2. subsequence ("fuzzy") match on label
 *   3. substring match on sublabel
 * An empty query returns ALL commands in their stable input order (no match
 * offsets). Ranking is deterministic: stable sort by tier, ties broken by label.
 *
 * Match offsets are returned in the SAME shape the backend uses
 * (``{ field, start, length }``). For prefix/substring they are exact
 * (start = indexOf, length = query length); for a fuzzy label match the run
 * starts at the first matched char with length 0 (no contiguous span).
 */
import type { CommandEntry, CommandMatch, RankedMatch } from "./types";

const TIER_LABEL_PREFIX = 0;
const TIER_LABEL_SUBSTRING = 1;
const TIER_LABEL_FUZZY = 2;
const TIER_SUBLABEL_SUBSTRING = 3;

/** True if every char of `needle` appears in `haystack` in order. */
function subsequenceStart(haystack: string, needle: string): number | null {
  if (needle.length === 0) return null;
  let firstIdx = -1;
  let n = 0;
  for (let h = 0; h < haystack.length && n < needle.length; h++) {
    if (haystack[h] === needle[n]) {
      if (n === 0) firstIdx = h;
      n++;
    }
  }
  return n === needle.length ? firstIdx : null;
}

interface Scored {
  tier: number;
  match?: CommandMatch;
}

function scoreEntry(entry: CommandEntry, q: string): Scored | null {
  const label = entry.label.toLowerCase();
  const sublabel = (entry.kind === "nav" ? entry.sublabel : (entry.sublabel ?? "")).toLowerCase();

  if (label.startsWith(q)) {
    return { tier: TIER_LABEL_PREFIX, match: { field: "label", start: 0, length: q.length } };
  }
  const labelIdx = label.indexOf(q);
  if (labelIdx >= 0) {
    return {
      tier: TIER_LABEL_SUBSTRING,
      match: { field: "label", start: labelIdx, length: q.length },
    };
  }
  const fuzzyStart = subsequenceStart(label, q);
  if (fuzzyStart !== null) {
    return {
      tier: TIER_LABEL_FUZZY,
      match: { field: "label", start: fuzzyStart, length: 0 },
    };
  }
  const subIdx = sublabel.indexOf(q);
  if (subIdx >= 0) {
    return {
      tier: TIER_SUBLABEL_SUBSTRING,
      match: { field: "sublabel", start: subIdx, length: q.length },
    };
  }
  return null;
}

/**
 * Filter + rank `commands` by `query`. Empty/blank query → all commands in
 * input order (score 0, no match). Otherwise only matching commands, ranked.
 */
export function filterCommands(
  commands: CommandEntry[],
  query: string,
): RankedMatch[] {
  const q = query.trim().toLowerCase();
  if (q.length === 0) {
    return commands.map((entry) => ({ entry, score: 0 }));
  }

  const scored: Array<{ rank: RankedMatch; order: number }> = [];
  commands.forEach((entry, order) => {
    const s = scoreEntry(entry, q);
    if (s) {
      scored.push({ rank: { entry, score: s.tier, match: s.match }, order });
    }
  });

  scored.sort((a, b) => {
    if (a.rank.score !== b.rank.score) return a.rank.score - b.rank.score;
    const byLabel = a.rank.entry.label.localeCompare(b.rank.entry.label);
    if (byLabel !== 0) return byLabel;
    return a.order - b.order;
  });

  return scored.map((s) => s.rank);
}
